import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { getPrincipalInfo } from 'lib/api/sgr';
import { SGR_STATES, SGR_TYPES } from 'lib/consts';
import { errorHandler } from 'lib/functions/errors';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import { formatDate } from 'lib/functions/utils';
import {
  Address,
  Business,
  BusinessUnit,
  Document,
  Warranty,
} from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import ClaimedWarrantyCard from '../ClaimedWarrantyCard';
import CreateWarrantyDialog, {
  WarrantyFormData,
} from '../CreateWarrantyDialog';
import DetailedRiskPremiumCalculator from '../DetailedRiskPremiumCalculator';
import PaymentDocument from '../PaymentDocument';
import ConfirmActionDialog from './Components/ConfirmActionDialog';
import GeneralInfo from './Components/GeneralInfo';
import ImportantDates from './Components/ImportantDates';
import PrincipalInfo from './Components/PrincipalInfo';

type Principal = {
  business: Business;
  unit: BusinessUnit;
  address: Address;
};

interface SummaryProps {
  warranty: Warranty & { documents?: Document[] };
  setIsEditing: (isEditing: boolean) => void;
  refreshWarranty: () => void;
}

const Summary: React.FC<SummaryProps> = ({
  warranty,
  setIsEditing,
  refreshWarranty,
}) => {
  const dispatch = useDispatch();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [initialValues, setInitialValues] = useState<WarrantyFormData>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const warrantyType =
    SGR_TYPES.find((type) => type.value === warranty.type)?.label ??
    warranty.type;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable,
    fixedRate: warranty.operationCostFixed,
    variableDiscount: warranty.discountRateVariable,
    fixedDiscount: warranty.discountRateFixed,
  });

  const showDocument = useMemo(() => {
    return warranty?.status === 'APPROVED' || warranty?.status === 'ACTIVE';
  }, [warranty.status]);

  const showCalculator = useMemo(() => {
    return (
      warranty?.status === 'QUOTED' ||
      warranty?.status === 'IN_REVIEW' ||
      warranty?.status === 'REJECTED'
    );
  }, [warranty.status]);

  const canEdit = useMemo(() => {
    return (
      warranty.status === 'REJECTED' ||
      warranty.status === 'APPROVED' ||
      warranty.status === 'IN_REVIEW'
    );
  }, [warranty.status]);

  const isActive = useMemo(
    () => warranty.status === 'ACTIVE',
    [warranty.status],
  );

  const menuOptions = useMemo(() => {
    const opts: { label: string; action: () => void }[] = [];
    if (canEdit) {
      opts.push({ label: 'Editar', action: () => setIsEditing(true) });
    }
    if (isActive) {
      opts.push({
        label: 'Siniestrar',
        action: () => setOpenClaimDialog(true),
      });
      opts.push({
        label: 'Anular',
        action: () => setOpenCancelDialog(true),
      });
      opts.push({
        label: 'Reemitir',
        action: () => {
          const prefillBusiness = warranty.businessIdentifier
            ? {
                identifier: warranty.businessIdentifier,
                businessName: '',
              }
            : null;
          setInitialValues({
            type: warranty.type,
            quotedAmount: warranty.quotedAmount?.toString() ?? '',
            quotedCurrency: warranty.quotedCurrency as 'UF' | 'USD' | 'CLP',
            startDate: dayjs(),
            endDate: null,
            tenderId: warranty.tenderId ?? '',
            principalRut: warranty.principalRut ?? '',
            unitCode: principal?.unit?.unitCode ?? '',
            business: prefillBusiness,
            description: warranty.description ?? '',
          });
          setOpenCreateDialog(true);
        },
      });
    }
    return opts;
  }, [canEdit, isActive, setIsEditing, warranty]);

  const amount = warranty.fixedAmount ?? warranty.quotedAmount;
  const daysDifference = Math.ceil(
    (new Date(warranty.endDate).getTime() -
      new Date(warranty.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  const fetchPrincipal = async () => {
    try {
      if (!warranty.id) return;
      const principalResponse = await getPrincipalInfo(dispatch, warranty.id);
      setPrincipal(principalResponse);
    } catch (error) {
      errorHandler(dispatch, error, 'Error fetching principal info');
    }
  };

  useEffect(() => {
    if (warranty.id) void fetchPrincipal();
  }, [warranty.id]);

  return (
    <>
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <GeneralInfo
              warrantyStatus={warrantyStatus}
              warrantyType={warrantyType}
              onOpenMenu={handleMenuOpen}
              onCloseMenu={handleMenuClose}
              menuAnchorEl={menuAnchorEl}
              menuOpen={menuOpen}
              menuOptions={menuOptions}
              quotedAmount={warranty.quotedAmount}
              quotedCurrency={warranty.quotedCurrency}
              tenderId={warranty.tenderId}
              description={warranty.description ?? 'Sin descripción'}
            />

            <ImportantDates
              quoteDate={formatDate(new Date(warranty.quoteDate), 'dd-mm-yyyy')}
              startDate={formatDate(new Date(warranty.startDate), 'dd-mm-yyyy')}
              endDate={formatDate(new Date(warranty.endDate), 'dd-mm-yyyy')}
            />

            <PrincipalInfo
              principal={principal}
              principalRut={warranty.principalRut}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {warranty.status === 'CLAIMED' && (
              <Box sx={{ mb: 2 }}>
                <ClaimedWarrantyCard warranty={warranty} />
              </Box>
            )}
            {showDocument && (
              <Box sx={{ mb: 2 }}>
                <PaymentDocument document={warranty.documents?.[0]} />
              </Box>
            )}

            {showCalculator && (
              <Box>
                <DetailedRiskPremiumCalculator
                  amount={amount}
                  days={daysDifference}
                  quotedCurrency={warranty.quotedCurrency}
                  type={warranty.type}
                  businessIdentifier={warranty.businessIdentifier}
                  rateControls={rateControls}
                  setRateControls={setRateControls}
                  useConcentrationRates={false}
                  canEditRates={false}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <ConfirmActionDialog
        type="cancel"
        warrantyRequestId={warranty.id}
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        refreshWarranty={refreshWarranty}
      />
      <ConfirmActionDialog
        type="claim"
        warrantyRequestId={warranty.id}
        open={openClaimDialog}
        onClose={() => setOpenClaimDialog(false)}
        refreshWarranty={refreshWarranty}
      />
      <CreateWarrantyDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fetchWarranties={refreshWarranty}
        initialValues={initialValues}
      />
    </>
  );
};

export default Summary;
